import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Stack,
  Typography,
  Alert,
  Snackbar,
  Divider,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { useCheckoutFlowStore } from "../../store/useCheckoutFlowStore";
import { useCartStore } from "../../store/useCartStore";

import { elevation } from "../../styles/shadows";
import PrimaryButton from "../common/PrimaryButton";
import { useAuthStore } from "../../store/useAuthStore";
import { useSyncStep } from "../../hooks/useSyncStep";
import { CartItem } from "../../types/common";
import { useKyc } from "../../hooks/useKyc";
import { getCurrentRouteStep } from "../../utils/getCurrentCheckoutStep";
import { useCart } from "../../hooks/useCart";
import { useOrderStore } from "../../store/useOrderStore";
import { useCheckOrderStatus } from "../../hooks/useCheckOrderStatus";
import { useSnackbarStore } from "../../store/useSnackbarStore";
import { handlePaymentSuccessService } from "../../service/handlePaymentSuccess.service";
import { useEffect, useState } from "react";

interface User {
  cusId: string;
  [key: string]: any;
}

interface StepValidation {
  isValid: boolean;
  redirectTo?: CheckoutStep;
  reason?: string;
}

interface CanProceedResult {
  canProceed: boolean;
  error?: string;
}

type CheckoutStep = "select" | "contact" | "kyc" | "payment";

interface StageConfig {
  secondary_label: string;
  onClickSecondary: () => void;
}

interface SwipeableCartDetailsProps {
  isOpen: boolean;
  toggleOpen: () => void;
}

const SwipeableCartDetails: React.FC<SwipeableCartDetailsProps> = ({
  isOpen,
  toggleOpen,
}) => {
  const cartItems = useCartStore((state) => state.cartItems);
  const increaseQuantity = useCartStore((state) => state.increaseQuantity);
  const decreaseQuantity = useCartStore((state) => state.decreaseQuantity);

  return (
    <>
      <Box display="flex" justifyContent="center">
        <IconButton
          size="small"
          onClick={toggleOpen}
          sx={{
            bgcolor: "secondary.main",
            borderRadius: "16px",
            color: "primary.main",
            width: 40,
            height: 6,
            py: 1.5,
            px: 4,
            mb: 1,
            "&:hover": {
              bgcolor: "primary.dark",
              color: "primary.contrastText",
            },
          }}
        >
          {isOpen ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
      </Box>

      {isOpen && (
        <Stack spacing={1} mb={2} px={1}>
          {cartItems.map((item: CartItem) => (
            <Stack
              key={item.packageId}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack>
                <Typography variant="body2">{item.packageName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.price.toLocaleString()} ₭
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton
                  size="small"
                  onClick={() => decreaseQuantity(item.packageId)}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" fontWeight="bold">
                  {item.quantity}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => increaseQuantity(item.packageId)}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          ))}
          <Divider />
        </Stack>
      )}
    </>
  );
};

const CartFooter: React.FC = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbarStore();
  const [errorMessage, setErrorMessage] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);

  const getTotalCartItems = useCartStore((state) => state.getTotalQuantity);
  const total = getTotalCartItems();
  // Auth state
  const {
    user,
    isAuthenticated,
  }: { user: User | null; isAuthenticated: boolean } = useAuthStore();

  // Checkout flow state
  const currentStep = useCheckoutFlowStore((state) => state.currentStep);
  const nextStep = useCheckoutFlowStore((state) => state.nextStep);
  const prevStep = useCheckoutFlowStore((state) => state.prevStep);
  const navigate = useNavigate();
  const getValidStepForUser = useCheckoutFlowStore(
    (state) => state.getValidStepForUser,
  );
  const goToStep = useCheckoutFlowStore((state) => state.goToStep);

  const localCart = useCartStore((state) => state.cartItems);
  const { clearCart } = useCartStore();
  const totalPrice = localCart.reduce((total, item) => {
    return total + (item.price || 0) * (item.quantity || 1);
  }, 0);
  const canProceedToNextStep = useCheckoutFlowStore(
    (state) => state.canProceedToNextStep,
  );
  const validateCurrentStep = useCheckoutFlowStore(
    (state) => state.validateCurrentStep,
  );

  const kycFormData = useCheckoutFlowStore((state) => state.kycFormData);
  const isKycVerified = useCheckoutFlowStore((state) => state.isKycVerified);

  const { submitKycMutate: submitKyc, isSubmitting: isSubmittingKyc } = useKyc(
    user?.cusId,
  );
  const syncStep = useSyncStep();
  const apiCart = useCart(user?.cusId || "");

  // Combined loading state
  const isButtonLoading =
    isSubmittingKyc || apiCart.addToCartLoading || disabledButton;

  const handlePrimaryAction = async (): Promise<void> => {
    const { canProceed, error }: CanProceedResult = canProceedToNextStep();
    // eslint-disable-next-line react-hooks/rules-of-hooks

    if (!canProceed) {
      console.warn("🚫 Cannot proceed:", error);
      setErrorMessage(error || "Cannot proceed to next step");
      return;
    }

    // ✅ Handle step transitions
    try {
      switch (currentStep) {
        case "kyc":
          await handleKycStep();
          break;
        case "payment": // Final Step After Payment - Clicked on Already Paid
          await handlePaymentStep();
          break;
        default:
          const next: CheckoutStep = nextStep();
          await syncStep(next);
      }
    } catch (error: any) {
      console.error("❌ Unexpected error:", error);
      setErrorMessage(error?.message || "An error occurred. Please try again.");
    }
  };

  const handleKycStep = async (): Promise<void> => {
    if (!isAuthenticated || !user) {
      setErrorMessage("Please sign in to continue");
      return;
    }

    if (!isKycVerified) {
      if (!kycFormData) {
        setErrorMessage("Please complete the KYC verification form");
        return;
      }

      return new Promise<void>((resolve, reject) => {
        submitKyc(kycFormData, {
          onSuccess: async () => {
            try {
              await queryClient.invalidateQueries({
                queryKey: ["order", user.cusId],
              });
              const next: CheckoutStep = nextStep();
              await syncStep(next);

              resolve();
            } catch (error) {
              reject(new Error("Failed to proceed. Please try again."));
            }
          },
          onError: (error: any) => {
            reject(
              new Error(error?.message || "Failed to submit KYC verification"),
            );
          },
        });
      });
    }

    const next: CheckoutStep = nextStep();
    await syncStep(next);
  };

  const { activeOrderId, clearActiveOrderId } = useOrderStore();

  const { mutate: checkOrderStatusMutate } = useCheckOrderStatus();
  const handlePaymentStep = async (): Promise<void> => {
    // check payment status
    if (!user) {
      setErrorMessage("Please sign in to continue");
      return;
    }
    setDisabledButton(true);

    try {
      // create universal toast and set status based on this part, render in payment page (on Payment Error, on Payment Succcess)
      if (activeOrderId) {
        checkOrderStatusMutate(activeOrderId, {
          // on payment success
          onSuccess: (data) => {
            if (!data.data)
              return showSnackbar("No order data found", "warning");
            if (data.code === "200" && data.data?.status === "success") {
              // use onOrderSuccess function to handle success flow
              handlePaymentSuccessService();
              return;
            }
            if (data.code === "400" && data.data?.status === "failed") {
              // clearActiveOrderId(); clear if no retry on error
              setErrorMessage("Payment failed. Please try again.");
              navigate("/", { replace: true });
              return;
            }
            return showSnackbar("Your order hasn't been paid yet", "warning");
          },
          onError: (error: any) => {
            // clearActiveOrderId(); clear if no retry on error
            showSnackbar(
              "Failed to check order status. Please try again... \n" +
                error?.message,
              "error",
            );
          },
        });
      }
      // navigate("/purchase", { replace: true });
    } catch (e: any) {
      showSnackbar(
        e?.message || "Failed to check order status. Please try again.",
        "error",
      );
    } finally {
      setTimeout(() => {
        setDisabledButton(false);
      }, 3000);
    }
  };

  const handleSecondaryAction = () => {
    if (currentStep === "payment") {
      // Cancel action
      clearCart();
      clearActiveOrderId();
      navigate("/", { replace: true });
      goToStep("select");
    }
    if (currentStep === "select") {
      clearCart();
      return;
    }
    const prev: CheckoutStep = prevStep();
    syncStep(prev);
  };

  const getButtonLabel = (): string => {
    if (currentStep === "payment") {
      return "Already Paid";
    }

    if (currentStep === "kyc") {
      return isSubmittingKyc ? "Verifying..." : "Continue to Payment";
    }

    const labels: Record<string, string> = {
      select: "Check Out",
      contact: "Next",
    };
    return labels[currentStep] || "Next";
  };

  const stageConfig: Record<CheckoutStep, StageConfig> = {
    select: {
      secondary_label: "Clear Cart",
      onClickSecondary: handleSecondaryAction,
    },
    contact: {
      secondary_label: "Back",
      onClickSecondary: handleSecondaryAction,
    },
    kyc: { secondary_label: "Back", onClickSecondary: handleSecondaryAction },
    payment: {
      secondary_label: "Cancel",
      onClickSecondary: handleSecondaryAction,
    },
  };

  const config = stageConfig[currentStep];
  const primary_label: string = getButtonLabel();

  // Get the step from URL params
  const searchParams = new URLSearchParams(window.location.search);

  const urlStep = searchParams.get("step");
  const pathname = window.location.pathname;

  useEffect(() => {
    const routeStep = getCurrentRouteStep();

    if (!routeStep) return;

    // If route step differs from store step, validate and sync
    if (routeStep !== currentStep) {
      // Special case: If user is on select route, always allow them to stay
      if (routeStep === "select") {
        goToStep("select");
        return;
      }

      // For other routes, check if user can access that step
      const validation = useCheckoutFlowStore
        .getState()
        .canAccessStep(routeStep);

      if (validation.canAccess) {
        // User can access this step, sync store to match route
        goToStep(routeStep);
      } else {
        // User cannot access this step, redirect to valid step
        console.warn(`Cannot access step ${routeStep}: ${validation.reason}`);
        const validStep = getValidStepForUser();
        syncStep(validStep);
      }
    }
  }, [urlStep, pathname, currentStep, goToStep, getValidStepForUser, syncStep]);

  const stepValidation: StepValidation = validateCurrentStep();
  if (!stepValidation.isValid) return null;
  return (
    <>
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "white",
          pt: 0.5,
          pb: 1,
          px: 1.5,
          boxShadow: elevation[4],
          zIndex: 1000,
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
        }}
      >
        {currentStep === "select" && total > 0 && (
          <SwipeableCartDetails
            isOpen={isOpen}
            toggleOpen={() => setIsOpen((prev) => !prev)}
          />
        )}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.5}
        >
          <Stack sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              lineHeight={1.2}
            >
              Total
            </Typography>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              lineHeight={1.2}
              noWrap
            >
              {totalPrice.toLocaleString()} ₭
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            {config?.secondary_label && (
              <PrimaryButton
                variant="outlined"
                size="small"
                onClick={config.onClickSecondary}
                disabled={isButtonLoading}
              >
                {config.secondary_label}
              </PrimaryButton>
            )}
            <PrimaryButton
              variant="contained"
              size="small"
              onClick={handlePrimaryAction}
              disabled={isButtonLoading}
              loading={isButtonLoading}
            >
              {primary_label}
            </PrimaryButton>
          </Stack>
        </Stack>
      </Box>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorMessage("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CartFooter;
